import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Icon } from 'views/components/molecules/Icon';
import { InputText } from 'views/components/atoms/InputText';
import { Color, FontSize, MAX_WIDTH } from 'views/constants';
import { Button } from 'views/components/atoms/Button';
import { Typography } from 'views/components/atoms/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { Checkbox } from 'views/components/atoms/Checkbox';
import { LinkText } from 'views/components/molecules/LinkText';
import { localeIds } from 'locals';
import { ErrorMessage } from 'views/components/molecules/ErrorMessage';
import { APIDomain } from 'models/API';
import { InfoMessage } from 'views/components/molecules/InfoMessage';

const Wrapper = styled.div`
  margin: 0;
  padding: 0 16px;
  background-color: ${Color.C6};
  border-bottom: 1px solid ${Color.C14};
`;

const InputWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
  padding: 16px 0;

  &:not(:first-of-type) {
    border-top: 1px solid ${Color.C14};
  }
`;

const TermsAcceptFontSize = FontSize.XXS;

const TermsLink = (chunks: React.ReactNode[]): React.ReactNode => (
  <LinkText
    key="terms"
    size={TermsAcceptFontSize}
    to={`${APIDomain.origin()}/terms`}
    anotherTab
  >
    {chunks}
  </LinkText>
);

const PrivacyPolicyLink = (chunks: React.ReactNode[]): React.ReactNode => (
  <LinkText
    key="privacy_policy"
    size={TermsAcceptFontSize}
    to={`${APIDomain.origin()}/terms/privacy`}
    anotherTab
  >
    {chunks}
  </LinkText>
);

interface InputAreaProps {
  email: string;
  defaultUsername: string;
  setPassword(value: string): void;
  setUsername(value: string): void;
  setTermsAccept(value: boolean): void;
  nameErrorMessage?: string;
  passwordErrorMessage?: string;
}

const InputArea: React.FC<InputAreaProps> = ({
  email,
  defaultUsername,
  setPassword,
  setUsername,
  setTermsAccept,
  nameErrorMessage,
  passwordErrorMessage,
}) => {
  const intl = useIntl();
  return (
    <Wrapper>
      <InputWrapper>
        <InputText
          label={<Icon type="mail" />}
          defaultValue={email}
          type="email"
          disabled
          required
        />
      </InputWrapper>
      <InputWrapper>
        <InputText
          label={<Icon type="pass" />}
          onChange={setPassword}
          placeholder={intl.formatMessage({
            id: localeIds.signUpForm.password,
          })}
          type="password"
          required
        />
        {passwordErrorMessage ? (
          <MessageWrapper>
            <ErrorMessage message={passwordErrorMessage} maxWidth={MAX_WIDTH} />
          </MessageWrapper>
        ) : null}
      </InputWrapper>
      <InputWrapper>
        <InputText
          label={<Icon type="user" />}
          onChange={setUsername}
          defaultValue={defaultUsername}
          placeholder={intl.formatMessage({
            id: localeIds.signUpForm.username,
          })}
          type="text"
          required
        />
        {nameErrorMessage ? (
          <MessageWrapper>
            <ErrorMessage message={nameErrorMessage} maxWidth={MAX_WIDTH} />
          </MessageWrapper>
        ) : null}
      </InputWrapper>
      <InputWrapper>
        <FormattedMessage
          id={localeIds.signUpForm.termsAccept}
          values={{
            terms: TermsLink,
            privacyPolicy: PrivacyPolicyLink,
          }}
        >
          {(chunks) => (
            <Checkbox
              id="terms"
              onChange={(name, value, checked): void => {
                setTermsAccept(checked);
              }}
              label={
                <Typography size={TermsAcceptFontSize} lineHeight="18px">
                  {chunks}
                </Typography>
              }
              required
            />
          )}
        </FormattedMessage>
      </InputWrapper>
    </Wrapper>
  );
};

const MessageWrapper = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  width: ${MAX_WIDTH};
  margin: auto;
  padding: 16px 0 28px;
`;

interface SignUpFormProps {
  email: string;
  defaultUsername?: string;
  errorMessage?: string;
  nameErrorMessage?: string;
  passwordErrorMessage?: string;
  disabled?: boolean;
  onSignUp(password: string, username: string, terms: boolean): void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  email,
  defaultUsername,
  errorMessage,
  nameErrorMessage,
  passwordErrorMessage,
  disabled = false,
  onSignUp,
}) => {
  const intl = useIntl();
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState(defaultUsername || '');
  const [termsAccept, setTermsAccept] = useState(false);
  const [showEmailVerifiedMessage, setShowEmailVerifiedMessage] =
    useState(true);
  const handleSignUp = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setShowEmailVerifiedMessage(false);
    onSignUp(password, username, termsAccept);
  };

  return (
    <form onSubmit={handleSignUp}>
      <InputArea
        email={email}
        defaultUsername={username}
        setPassword={setPassword}
        setUsername={setUsername}
        setTermsAccept={setTermsAccept}
        nameErrorMessage={nameErrorMessage}
        passwordErrorMessage={passwordErrorMessage}
      />

      {showEmailVerifiedMessage ? (
        <MessageWrapper>
          <InfoMessage
            message={intl.formatMessage({
              id: localeIds.signUpForm.emailValidatedMessage,
            })}
            maxWidth={MAX_WIDTH}
          />
        </MessageWrapper>
      ) : null}
      {errorMessage ? (
        <MessageWrapper>
          <ErrorMessage message={errorMessage} maxWidth={MAX_WIDTH} />
        </MessageWrapper>
      ) : null}

      <ButtonWrapper>
        <Button type="submit" style="primary" disabled={disabled}>
          <FormattedMessage id={localeIds.signUpForm.submit} />
        </Button>
      </ButtonWrapper>
    </form>
  );
};
